import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle,
  ArrowLeftRight,
  CheckSquare,
  CreditCard,
  Eraser,
  FileText,
  RefreshCcw,
  Search,
  Sparkles,
  Truck,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchOpenClientReceivables,
  applyCollectionAllocations,
} from '../utils/salesReceivables';
import {
  buildProviderPaymentItems,
  buildSuggestedAllocations,
  clampMoney,
  fetchOpenProviderPurchases,
  normalizeMoney,
  parseProviderPaymentApplications,
  recomputeProviderPurchaseStatuses,
  sumAllocationAmounts,
} from '../utils/accountReconciliation';
import {
  fetchActiveOrganizationClientsWithBalances,
  getClientBalanceValue,
} from '../utils/clientBalances';
import { fetchActiveOrganizationProvidersWithBalances } from '../utils/providerBalances';

const EPSILON = 0.000001;

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  if (!withTime) {
    return date.toLocaleDateString('es-AR', { timeZone: 'UTC' });
  }
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function ConciliacionCuentasCorrientes() {
  const { profile } = useAuth();

  const [mode, setMode] = useState('clientes');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  const [fechaDesde, setFechaDesde] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState(() => new Date().toISOString().split('T')[0]);

  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [movements, setMovements] = useState([]);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentSource, setDocumentSource] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadMasters();
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadMovements();
  }, [profile?.organization_id, mode, fechaDesde, fechaHasta]);

  useEffect(() => {
    setSelectedMovementId(null);
    setSelectedEntityId('');
    setDocuments([]);
    setDocumentSource([]);
  }, [mode]);

  const clientMap = useMemo(
    () => new Map((clientes || []).map((client) => [client.id, client])),
    [clientes],
  );
  const providerMap = useMemo(
    () => new Map((proveedores || []).map((provider) => [provider.id, provider])),
    [proveedores],
  );

  const selectedMovement = useMemo(
    () => movements.find((movement) => movement.id === selectedMovementId) || null,
    [movements, selectedMovementId],
  );

  const currentEntityOptions = mode === 'clientes' ? clientes : proveedores;

  useEffect(() => {
    if (!selectedMovement) {
      setSelectedEntityId('');
      setDocuments([]);
      setDocumentSource([]);
      return;
    }

    setSelectedEntityId(selectedMovement.entidad_id || '');
  }, [selectedMovement]);

  useEffect(() => {
    if (!profile?.organization_id || !selectedMovement || !selectedEntityId) {
      setDocuments([]);
      setDocumentSource([]);
      return;
    }

    loadDocuments();
  }, [profile?.organization_id, mode, selectedMovement?.id, selectedEntityId]);

  const kpis = useMemo(() => {
    const pendingAmount = movements.reduce(
      (acc, movement) => acc + Math.max(0, Number(movement.pending_amount) || 0),
      0,
    );
    return {
      pendingCount: movements.length,
      pendingAmount,
      docsCount: documents.length,
    };
  }, [documents.length, movements]);

  const filteredMovements = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return movements;

    return movements.filter((movement) => {
      const entityName =
        mode === 'clientes'
          ? clientMap.get(movement.entidad_id)?.full_name || ''
          : providerMap.get(movement.entidad_id)?.razon_social || '';

      return (
        String(movement.descripcion || '').toLowerCase().includes(needle) ||
        String(entityName).toLowerCase().includes(needle) ||
        String(movement.banks?.name || '').toLowerCase().includes(needle)
      );
    });
  }, [clientMap, mode, movements, providerMap, query]);

  async function loadMasters() {
    try {
      const orgId = profile.organization_id;
      const [clientsResponse, providersResponse] = await Promise.all([
        fetchActiveOrganizationClientsWithBalances({
          organizationId: orgId,
          supabaseClient: supabase,
        }),
        fetchActiveOrganizationProvidersWithBalances({
          organizationId: orgId,
          supabaseClient: supabase,
        }),
      ]);

      if (clientsResponse.error) throw clientsResponse.error;
      if (providersResponse.error) throw providersResponse.error;

      setClientes(clientsResponse.data || []);
      setProveedores(providersResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar clientes y proveedores');
    }
  }

  async function loadMovements() {
    setLoading(true);
    try {
      const orgId = profile.organization_id;
      const start = `${fechaDesde}T00:00:00.000Z`;
      const end = `${fechaHasta}T23:59:59.999Z`;
      const category = mode === 'clientes' ? 'Cobro' : 'Pago';
      const entityType = mode === 'clientes' ? 'Cliente' : 'Proveedor';

      const { data, error } = await supabase
        .from('movimientos_financieros')
        .select('id, created_at, fecha_operacion, descripcion, monto, entidad_id, entidad_tipo, documento_id, banks(name), sitios(nombre)')
        .eq('organization_id', orgId)
        .eq('categoria', category)
        .eq('entidad_tipo', entityType)
        .eq('is_active', true)
        .gte('fecha_operacion', start)
        .lte('fecha_operacion', end)
        .order('fecha_operacion', { ascending: false });

      if (error) throw error;

      const baseMovements = (data || []).map((movement) => ({
        ...movement,
        amount: Math.max(0, Number(movement.monto) || 0),
      }));

      let pendingMovements = [];

      if (mode === 'clientes') {
        const movementIds = baseMovements.map((movement) => movement.id);
        const { data: applications, error: applicationsError } = movementIds.length
          ? await supabase
              .from('sales_receivable_applications')
              .select('financial_movement_id, amount')
              .in('financial_movement_id', movementIds)
          : { data: [], error: null };

        if (applicationsError) throw applicationsError;

        const appliedByMovement = new Map();
        (applications || []).forEach((application) => {
          const current = appliedByMovement.get(application.financial_movement_id) || 0;
          appliedByMovement.set(
            application.financial_movement_id,
            current + Math.max(0, Number(application.amount) || 0),
          );
        });

        pendingMovements = baseMovements
          .map((movement) => {
            const appliedAmount = Math.max(
              0,
              Number(appliedByMovement.get(movement.id) || 0),
            );
            const pendingAmount = Math.max(0, movement.amount - appliedAmount);
            return {
              ...movement,
              applied_amount: appliedAmount,
              pending_amount: pendingAmount,
            };
          })
          .filter((movement) => movement.applied_amount <= EPSILON && movement.pending_amount > EPSILON);
      } else {
        const documentIds = baseMovements.map((movement) => movement.documento_id).filter(Boolean);
        const { data: paymentDocs, error: paymentDocsError } = documentIds.length
          ? await supabase
              .from('compras')
              .select('id, items')
              .in('id', documentIds)
          : { data: [], error: null };

        if (paymentDocsError) throw paymentDocsError;

        const applicationsByDocument = new Map();
        (paymentDocs || []).forEach((paymentDoc) => {
          const appliedAmount = parseProviderPaymentApplications(paymentDoc.items).reduce(
            (acc, application) => acc + Math.max(0, Number(application.amount) || 0),
            0,
          );
          applicationsByDocument.set(paymentDoc.id, appliedAmount);
        });

        pendingMovements = baseMovements
          .map((movement) => {
            const appliedAmount = Math.max(
              0,
              Number(applicationsByDocument.get(movement.documento_id) || 0),
            );
            const pendingAmount = Math.max(0, movement.amount - appliedAmount);
            return {
              ...movement,
              applied_amount: appliedAmount,
              pending_amount: pendingAmount,
            };
          })
          .filter((movement) => movement.applied_amount <= EPSILON && movement.pending_amount > EPSILON);
      }

      setMovements(pendingMovements);
      setSelectedMovementId((current) =>
        pendingMovements.some((movement) => movement.id === current) ? current : null,
      );
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar los movimientos pendientes');
    } finally {
      setLoading(false);
    }
  }

  async function loadDocuments() {
    setLoadingDocuments(true);
    try {
      if (mode === 'clientes') {
        const response = await fetchOpenClientReceivables({
          organizationId: profile.organization_id,
          clientId: selectedEntityId,
        });
        if (response.error) throw response.error;

        const mappedDocuments = (response.data || []).map((document) => ({
          id: document.id,
          title:
            document.sale_number ||
            `Venta #${String(document.sale_id || document.id).slice(-8).toUpperCase()}`,
          kindLabel: 'Venta',
          numberLabel: document.sale_number || '',
          dateLabel: formatDate(document.sale_created_at || document.created_at),
          paymentConditionLabel: document.payment_condition_label || 'Cuenta corriente',
          dueLabel: document.payment_due_date ? formatDate(document.payment_due_date) : null,
          description: document.description || 'Venta pendiente de cancelación.',
          open_amount: Math.max(0, Number(document.pending_amount) || 0),
        }));

        setDocumentSource(response.data || []);
        setDocuments(
          buildSuggestedAllocations(
            mappedDocuments,
            selectedMovement?.pending_amount || 0,
            'open_amount',
          ),
        );
      } else {
        const response = await fetchOpenProviderPurchases({
          organizationId: profile.organization_id,
          providerId: selectedEntityId,
          supabaseClient: supabase,
        });
        if (response.error) throw response.error;

        const mappedDocuments = (response.data || []).map((document) => ({
          id: document.id,
          title:
            document.numero_comprobante ||
            `${document.tipo_comprobante || 'Compra'} #${String(document.id).slice(-8).toUpperCase()}`,
          kindLabel: document.tipo_comprobante || 'Compra',
          numberLabel: document.numero_comprobante || '',
          dateLabel: formatDate(document.fecha || document.created_at),
          paymentConditionLabel: document.condicion_pago || 'Cuenta corriente',
          dueLabel: null,
          description: document.observaciones || 'Compra pendiente de pago.',
          open_amount: Math.max(0, Number(document.open_amount) || 0),
        }));

        setDocumentSource(response.data || []);
        setDocuments(
          buildSuggestedAllocations(
            mappedDocuments,
            selectedMovement?.pending_amount || 0,
            'open_amount',
          ),
        );
      }
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar los comprobantes a conciliar');
      setDocuments([]);
      setDocumentSource([]);
    } finally {
      setLoadingDocuments(false);
    }
  }

  const handleAllocationChange = (documentId, rawValue) => {
    setDocuments((prev) =>
      prev.map((document) =>
        document.id === documentId
          ? {
              ...document,
              allocation: clampMoney(rawValue, document.open_amount),
            }
          : document,
      ),
    );
  };

  const handleAutoAllocate = () => {
    if (!selectedMovement || documents.length === 0) return;
    setDocuments((prev) =>
      buildSuggestedAllocations(prev, selectedMovement.pending_amount || 0, 'open_amount'),
    );
  };

  const handleClearAllocations = () => {
    if (documents.length === 0) return;
    setDocuments((prev) => prev.map((document) => ({ ...document, allocation: 0 })));
  };

  const handleConciliar = async () => {
    if (!selectedMovement?.id) {
      return toast.error('Seleccioná un movimiento para conciliar');
    }

    if (!selectedEntityId) {
      return toast.error(`Seleccioná un ${mode === 'clientes' ? 'cliente' : 'proveedor'}`);
    }

    const totalOpen = documents.reduce(
      (acc, document) => acc + Math.max(0, Number(document.open_amount) || 0),
      0,
    );
    const requiredAllocation = Math.min(
      Math.max(0, Number(selectedMovement.pending_amount) || 0),
      totalOpen,
    );
    const allocated = sumAllocationAmounts(documents);

    if (allocated + EPSILON < requiredAllocation) {
      return toast.error('Todavía falta asignar parte del movimiento');
    }

    setSaving(true);
    try {
      if (mode === 'clientes') {
        const allocations = documents
          .map((document) => ({
            receivable_id: document.id,
            amount: Math.max(0, normalizeMoney(document.allocation)),
          }))
          .filter((allocation) => allocation.receivable_id && allocation.amount > EPSILON);

        await applyCollectionAllocations({
          financialMovementId: selectedMovement.id,
          allocations,
        });
      } else {
        if (!selectedMovement.documento_id) {
          throw new Error('El pago seleccionado no tiene comprobante vinculado');
        }

        const allocations = documents
          .map((document) => ({
            purchase_id: document.id,
            amount: Math.max(0, normalizeMoney(document.allocation)),
          }))
          .filter((allocation) => allocation.purchase_id && allocation.amount > EPSILON);

        const paymentItems = buildProviderPaymentItems(allocations, documentSource);

        const { error: updateError } = await supabase
          .from('compras')
          .update({ items: paymentItems })
          .eq('id', selectedMovement.documento_id);
        if (updateError) throw updateError;

        await recomputeProviderPurchaseStatuses({
          organizationId: profile.organization_id,
          providerId: selectedEntityId,
          purchaseIds: allocations.map((allocation) => allocation.purchase_id),
          supabaseClient: supabase,
        });
      }

      toast.success('Conciliación registrada');
      setSelectedMovementId(null);
      setSelectedEntityId('');
      setDocuments([]);
      setDocumentSource([]);
      await loadMovements();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar la conciliación');
    } finally {
      setSaving(false);
    }
  };

  const selectedEntity =
    mode === 'clientes'
      ? clientMap.get(selectedEntityId) || null
      : providerMap.get(selectedEntityId) || null;

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-[1600px] w-full mx-auto space-y-6 animate-in fade-in h-full flex flex-col pt-16 lg:pt-6 bg-[#F8FAFC]">
      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <ArrowLeftRight className="text-[#51A7FD]" />
            Conciliación de Cuentas Corrientes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vinculá cobros o pagos ya registrados con sus ventas o compras pendientes.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
          <button
            type="button"
            onClick={() => setMode('clientes')}
            className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
              mode === 'clientes'
                ? 'bg-[#0B2A36] text-white'
                : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
          >
            Clientes
          </button>
          <button
            type="button"
            onClick={() => setMode('proveedores')}
            className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
              mode === 'proveedores'
                ? 'bg-[#0B2A36] text-white'
                : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
          >
            Proveedores
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 shrink-0">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Modo activo
            </p>
            {mode === 'clientes' ? (
              <UserRound size={16} className="text-[#51A7FD]" />
            ) : (
              <Truck size={16} className="text-[#F05A57]" />
            )}
          </div>
          <p className="mt-1.5 text-xl font-black text-slate-800 tracking-tight uppercase">
            {mode === 'clientes' ? 'Cobros' : 'Pagos'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Cuenta corriente
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Movimientos
            </p>
            <Wallet size={16} className="text-amber-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {kpis.pendingCount}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Pendientes de conciliar
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Importe
            </p>
            <CreditCard size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            $ {formatMoney(kpis.pendingAmount)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Sin asignar
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Comprobantes
            </p>
            <FileText size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {kpis.docsCount}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Abiertos del titular
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6 flex-1 min-h-0">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[520px]">
          <div className="px-5 py-4 border-b border-white/10 bg-[#0B2A36] flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-[11px] font-black text-white uppercase tracking-widest flex items-center gap-2">
                <ArrowLeftRight size={16} className="text-[#3FF2AA]" />
                {mode === 'clientes' ? 'Movimientos de Cobro' : 'Movimientos de Pago'}
              </h2>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/60">
                Solo pendientes de conciliar
              </p>
            </div>

            <button
              type="button"
              onClick={loadMovements}
              className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <RefreshCcw size={14} />
              Actualizar
            </button>
          </div>

          <div className="p-4 border-b border-slate-100 bg-slate-50/70 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1 space-y-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Desde</label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(event) => setFechaDesde(event.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]"
              />
            </div>
            <div className="md:col-span-1 space-y-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Hasta</label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(event) => setFechaHasta(event.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]"
              />
            </div>
            <div className="relative md:col-span-1 space-y-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Titular, banco, descripción..."
                  className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-[10px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]"
                />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="p-3 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-24 bg-slate-50 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : filteredMovements.length === 0 ? (
              <div className="p-12 text-center flex flex-col items-center gap-2 text-slate-400">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center">
                  <Wallet size={24} className="opacity-60" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Sin movimientos pendientes
                </p>
                <p className="text-[10px] font-medium max-w-[260px]">
                  {query ? 'Probá con otro término o ajustá el rango de fechas.' : 'Todos los movimientos del período ya están conciliados.'}
                </p>
              </div>
            ) : (
              filteredMovements.map((movement) => {
                const isSelected = movement.id === selectedMovementId;
                const entity =
                  mode === 'clientes'
                    ? clientMap.get(movement.entidad_id)
                    : providerMap.get(movement.entidad_id);
                const entityLabel =
                  mode === 'clientes'
                    ? entity?.full_name || 'Cliente no resuelto'
                    : entity?.razon_social || 'Proveedor no resuelto';

                return (
                  <button
                    key={movement.id}
                    type="button"
                    onClick={() => setSelectedMovementId(movement.id)}
                    className={`w-full text-left p-4 rounded-2xl border transition-all ${
                      isSelected
                        ? 'border-[#51A7FD] bg-blue-50/60 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                          {formatDate(movement.fecha_operacion || movement.created_at, true)}
                        </p>
                        <p className="mt-1 text-[12px] font-black uppercase tracking-tight text-slate-800 truncate">
                          {entityLabel}
                        </p>
                        <p className="mt-1 text-[11px] font-bold text-slate-500 line-clamp-2">
                          {movement.descripcion || 'Sin descripción'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="px-2.5 py-1 rounded-xl bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                            {movement.banks?.name || 'Caja'}
                          </span>
                          <span className="px-2.5 py-1 rounded-xl bg-white border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-500">
                            {movement.sitios?.nombre || 'Administración Central'}
                          </span>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <p className={`text-[14px] font-black tracking-tight ${mode === 'clientes' ? 'text-emerald-600' : 'text-rose-600'}`}>
                          $ {formatMoney(movement.pending_amount)}
                        </p>
                        <p className="mt-1 text-[8px] font-black uppercase tracking-widest text-slate-400">
                          Importe a conciliar
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[520px]">
          <div className="px-5 py-4 border-b border-white/10 bg-[#0B2A36]">
            <h2 className="text-[11px] font-black text-white uppercase tracking-widest flex items-center gap-2">
              <CheckSquare size={16} className="text-[#3FF2AA]" />
              Aplicación sobre cuenta corriente
            </h2>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/60">
              Elegí el titular y asigná el movimiento
            </p>
          </div>

          <div className="p-4 border-b border-slate-100 bg-slate-50/70 space-y-3">
            <div className="space-y-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                {mode === 'clientes' ? 'Cliente' : 'Proveedor'}
              </label>
              <select
                value={selectedEntityId}
                onChange={(event) => setSelectedEntityId(event.target.value)}
                disabled={!selectedMovement || Boolean(selectedMovement?.entidad_id)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-3 text-[10px] font-black uppercase tracking-tight outline-none disabled:bg-slate-100 disabled:text-slate-400"
              >
                <option value="">
                  {mode === 'clientes' ? 'Seleccionar cliente...' : 'Seleccionar proveedor...'}
                </option>
                {currentEntityOptions.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {mode === 'clientes'
                      ? `${entity.full_name} (${formatMoney(getClientBalanceValue(entity))})`
                      : `${entity.razon_social} (${formatMoney(entity.saldo || 0)})`}
                  </option>
                ))}
              </select>
              {selectedMovement?.entidad_id ? (
                <p className="text-[10px] font-bold text-slate-400">
                  El titular se toma del movimiento ya registrado en tesorería.
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                  Movimiento
                </p>
                <p className="mt-1 text-[16px] font-black text-slate-800">
                  $ {formatMoney(selectedMovement?.pending_amount || 0)}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                  Titular
                </p>
                <p className="mt-1 text-[12px] font-black text-slate-800 uppercase truncate">
                  {mode === 'clientes'
                    ? selectedEntity?.full_name || 'Sin selección'
                    : selectedEntity?.razon_social || 'Sin selección'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!selectedMovement ? (
              <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center text-slate-400 gap-2">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center">
                  <ArrowLeftRight size={22} className="opacity-60" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Seleccioná un movimiento
                </p>
                <p className="text-[10px] font-medium max-w-[260px]">
                  Elegí del panel izquierdo el cobro o pago que querés vincular a comprobantes.
                </p>
              </div>
            ) : loadingDocuments ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-28 bg-slate-50 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : documents.length === 0 ? (
              <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center text-slate-400 gap-2">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center">
                  <FileText size={22} className="opacity-60" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Sin comprobantes abiertos
                </p>
                <p className="text-[10px] font-medium max-w-[260px]">
                  Este {mode === 'clientes' ? 'cliente' : 'proveedor'} no tiene {mode === 'clientes' ? 'ventas' : 'compras'} con saldo pendiente.
                </p>
              </div>
            ) : (
              documents.map((document) => (
                <div
                  key={document.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_180px] gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-1 rounded-xl bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                          {document.kindLabel}
                        </span>
                        {document.numberLabel ? (
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            {document.numberLabel}
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-2 text-[13px] font-black text-slate-800 uppercase tracking-tight">
                        {document.title}
                      </p>

                      <div className="mt-2 flex flex-wrap gap-3 text-[10px] font-bold text-slate-500 uppercase">
                        {document.dateLabel ? <span>{document.dateLabel}</span> : null}
                        {document.paymentConditionLabel ? <span>{document.paymentConditionLabel}</span> : null}
                        {document.dueLabel ? <span>Vence {document.dueLabel}</span> : null}
                      </div>

                      {document.description ? (
                        <p className="mt-2 text-[11px] font-bold text-slate-500">
                          {document.description}
                        </p>
                      ) : null}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                        Pendiente
                      </p>
                      <p className="mt-1 text-[16px] font-black text-slate-800">
                        $ {formatMoney(document.open_amount)}
                      </p>

                      <label className="mt-3 block text-[8px] font-black uppercase tracking-widest text-slate-400">
                        Aplicar
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={document.allocation ?? 0}
                        onChange={(event) => handleAllocationChange(document.id, event.target.value)}
                        className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-black text-slate-800 outline-none"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-100 bg-white space-y-3">
            {selectedMovement && documents.length > 0 ? (() => {
              const assigned = sumAllocationAmounts(documents);
              const pending = Math.max(0, Number(selectedMovement.pending_amount) || 0);
              const totalOpen = documents.reduce(
                (acc, document) => acc + Math.max(0, Number(document.open_amount) || 0),
                0,
              );
              const requiredAllocation = Math.min(pending, totalOpen);
              const remaining = Math.max(0, requiredAllocation - assigned);
              const excess = Math.max(0, assigned - pending);
              const pct = pending > EPSILON ? Math.min(100, (assigned / pending) * 100) : 0;
              const isMatch = Math.abs(assigned - requiredAllocation) < EPSILON;
              const isOver = excess > EPSILON;
              const barColor = isOver ? 'bg-rose-500' : isMatch ? 'bg-[#3FF2AA]' : 'bg-amber-400';

              return (
                <>
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-slate-500">
                      Asignado <span className="text-slate-800">$ {formatMoney(assigned)}</span> / Pendiente $ {formatMoney(pending)}
                    </span>
                    <span className={`${isOver ? 'text-rose-600' : isMatch ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {isOver
                        ? `Excedido $ ${formatMoney(excess)}`
                        : isMatch
                        ? 'Listo para conciliar'
                        : `Falta $ ${formatMoney(remaining)}`}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${barColor} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {isOver && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-rose-600">
                      <AlertTriangle size={12} />
                      El total asignado supera el importe del movimiento.
                    </div>
                  )}
                </>
              );
            })() : null}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleAutoAllocate}
                  disabled={!selectedMovement || documents.length === 0}
                  className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Distribuir automaticamente desde el comprobante mas antiguo"
                >
                  <Sparkles size={12} /> Auto asignar
                </button>
                <button
                  type="button"
                  onClick={handleClearAllocations}
                  disabled={!selectedMovement || documents.length === 0}
                  className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Eraser size={12} /> Limpiar
                </button>
                {selectedMovement ? (
                  <button
                    type="button"
                    onClick={() => setSelectedMovementId(null)}
                    className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5"
                  >
                    <X size={12} /> Cancelar
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleConciliar}
                disabled={saving || !selectedMovement || documents.length === 0}
                className="px-5 py-3 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:brightness-95 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCcw size={14} className="animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckSquare size={14} />
                    Conciliar movimiento
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
